import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { supabase } from '../lib/supabase';
import { contractLockService } from '../services/contractLockService';
import { Shield, Check, X, Lock, Unlock, Clock, User, FileText, Loader2, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface UnlockRequestWithDetails {
  id: string;
  customer_id: string;
  requested_by: string;
  reason: string;
  status: string;
  created_at: string;
  customer: {
    full_name: string;
    cpf: string;
  };
  requester: {
    email: string;
  };
}

export default function AdminUnlockPanel() {
  const queryClient = useQueryClient();
  const [selectedRequest, setSelectedRequest] = useState<string | null>(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [filter, setFilter] = useState<'pending' | 'all'>('pending');

  const { data: userRole, isLoading: roleLoading } = useQuery(
    ['user-role'],
    async () => {
      const { data: { user } } = await supabase.auth.getUser();
      return user ? contractLockService.getUserRole(user.id) : null;
    }
  );

  const { data: requests, isLoading: requestsLoading } = useQuery(
    ['unlock-requests', filter],
    async () => {
      let query = supabase
        .from('unlock_requests')
        .select(`
          *,
          customer:customers(full_name, cpf)
        `)
        .order('created_at', { ascending: false });

      if (filter === 'pending') {
        query = query.eq('status', 'pending');
      }

      const { data, error } = await query;
      if (error) throw error;

      const requestsWithEmails = await Promise.all(
        (data || []).map(async (req) => {
          const { data: userData } = await supabase.auth.admin.getUserById(req.requested_by);
          return {
            ...req,
            requester: {
              email: userData?.user?.email || 'Desconhecido'
            }
          };
        })
      );

      return requestsWithEmails as UnlockRequestWithDetails[];
    },
    {
      enabled: userRole?.role === 'admin'
    }
  );

  const approveMutation = useMutation(
    async (requestId: string) => {
      return contractLockService.approveUnlockRequest(requestId, reviewNotes);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['unlock-requests']);
        queryClient.invalidateQueries(['contract-lock']);
        setSelectedRequest(null);
        setReviewNotes('');
      }
    }
  );

  const rejectMutation = useMutation(
    async (requestId: string) => {
      if (!reviewNotes.trim()) {
        throw new Error('Informe o motivo da rejeição');
      }
      return contractLockService.rejectUnlockRequest(requestId, reviewNotes);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['unlock-requests']);
        setSelectedRequest(null);
        setReviewNotes('');
      }
    }
  );

  if (roleLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (userRole?.role !== 'admin') {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
        <div className="flex items-center gap-3">
          <AlertCircle className="w-6 h-6 text-yellow-600" />
          <div>
            <h3 className="font-semibold text-yellow-800">Acesso Restrito</h3>
            <p className="text-sm text-yellow-700 mt-1">
              Apenas administradores podem acessar este painel.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const pendingCount = requests?.filter(r => r.status === 'pending').length || 0;

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Shield className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Painel Administrativo</h2>
              <p className="text-sm text-gray-600">Gerenciar solicitações de desbloqueio</p>
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => setFilter('pending')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === 'pending'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Pendentes ({pendingCount})
            </button>
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Todas
            </button>
          </div>
        </div>

        {requestsLoading ? (
          <div className="flex justify-center items-center h-48">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : requests && requests.length > 0 ? (
          <div className="space-y-4">
            {requests.map((request) => (
              <div
                key={request.id}
                className={`border rounded-lg p-4 transition-all ${
                  request.status === 'pending'
                    ? 'border-yellow-300 bg-yellow-50'
                    : request.status === 'approved'
                    ? 'border-green-300 bg-green-50'
                    : 'border-red-300 bg-red-50'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <FileText className="w-5 h-5 text-gray-600" />
                      <h3 className="font-semibold text-gray-800">
                        {request.customer?.full_name || 'Cliente não encontrado'}
                      </h3>
                      <span
                        className={`px-2 py-1 rounded text-xs font-medium ${
                          request.status === 'pending'
                            ? 'bg-yellow-200 text-yellow-800'
                            : request.status === 'approved'
                            ? 'bg-green-200 text-green-800'
                            : 'bg-red-200 text-red-800'
                        }`}
                      >
                        {request.status === 'pending'
                          ? 'Pendente'
                          : request.status === 'approved'
                          ? 'Aprovado'
                          : 'Rejeitado'}
                      </span>
                    </div>

                    <div className="space-y-2 text-sm text-gray-700">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-gray-400" />
                        <span>Solicitado por: {request.requester.email}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-gray-400" />
                        <span>
                          {format(new Date(request.created_at), "dd 'de' MMMM 'de' yyyy 'às' HH:mm", {
                            locale: ptBR
                          })}
                        </span>
                      </div>
                      <div className="bg-white border rounded-lg p-3 mt-2">
                        <p className="text-xs text-gray-500 mb-1">Motivo:</p>
                        <p className="text-sm text-gray-800">{request.reason}</p>
                      </div>
                    </div>

                    {request.status === 'pending' && selectedRequest === request.id && (
                      <div className="mt-4 space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Notas da revisão (opcional)
                          </label>
                          <textarea
                            value={reviewNotes}
                            onChange={(e) => setReviewNotes(e.target.value)}
                            placeholder="Adicione observações sobre sua decisão..."
                            className="w-full px-3 py-2 border rounded-lg text-sm resize-none"
                            rows={3}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {request.status === 'pending' && (
                    <div className="flex gap-2 ml-4">
                      {selectedRequest === request.id ? (
                        <>
                          <button
                            onClick={() => approveMutation.mutate(request.id)}
                            disabled={approveMutation.isLoading}
                            className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center gap-2 text-sm"
                          >
                            <Check className="w-4 h-4" />
                            Aprovar
                          </button>
                          <button
                            onClick={() => rejectMutation.mutate(request.id)}
                            disabled={rejectMutation.isLoading}
                            className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2 text-sm"
                          >
                            <X className="w-4 h-4" />
                            Rejeitar
                          </button>
                          <button
                            onClick={() => {
                              setSelectedRequest(null);
                              setReviewNotes('');
                            }}
                            className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm"
                          >
                            Cancelar
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => setSelectedRequest(request.id)}
                          className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                        >
                          Revisar
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500">
            <Lock className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>
              {filter === 'pending'
                ? 'Nenhuma solicitação pendente no momento.'
                : 'Nenhuma solicitação encontrada.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
